import { onKeyDownSubmit } from 'accessibility/eventHandlers'
import MessageCloseButton from 'components/MessageCloseButton'
import { DEFAULT_WIDGET_ICON_WIDTH } from 'constants/controller'
import useActiveCampaign from 'hooks/useActiveCampaign'
import useIsBotMessage from 'hooks/useIsBotMessage'
import { useRecipientResolution } from 'hooks/useRecipientResolution'
import useResizeObserver from 'hooks/useResizeObserver'
import useUnreadMessagesForActiveConversation from 'hooks/useUnreadMessagesForActiveConversation'
import useWidgetAlignment from 'hooks/useWidgetAlignment'
import { triggerPlaybookClickedAPIEvent, triggerPlaybookDismissedAPIEvent } from 'modules/api/events'
import { storeAnnouncementCampaignCompleted } from 'modules/campaigns'
import { markConversationAsRead } from 'modules/conversations'
import { hasActiveConversationBeenInteractedWith } from 'modules/conversations/selectors'
import { getMessagePreviewText, truncateMessagePreview } from 'modules/message/utils'
import { resetControllerSize, setControllerDimensions } from 'modules/view'
import { toggleChat } from 'modules/view/actions'
import { widgetIconVisible } from 'modules/view/selectors'
import { equals, isEmpty, pathOr } from 'ramda'
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { WidgetAlignment } from 'shared/constants/frames'
import { Message } from 'types/message'
import { GDPRState } from 'types/session'
import { getNewMessageAriaLabel } from 'utils/a11y'

import './style.css'

const PREVIEW_MESSAGE_LENGTH_LIMIT = 100
const PREVIEW_MESSAGE_WIDTH_PADDING = 40
const PREVIEW_MESSAGE_HEIGHT_PADDING = 36

const MessagePreview = () => {
  const [previewRefRect, previewRef] = useResizeObserver()
  const dispatch = useDispatch()
  const [latestMessageToPreview, setLatestMessage] = useState<Partial<Message> | null>(null)

  const { activeConversation } = useSelector(state =>
    state.conversations
  )

  const gdpr: GDPRState = useSelector(state => state.session.gdpr)

  const unreadMessages = useUnreadMessagesForActiveConversation()
  const activeCampaign = useActiveCampaign()
  const widgetIconIsVisible = useSelector(widgetIconVisible)

  const open = useSelector(state =>
    state.view.chatOpen
  )

  const alignment = useWidgetAlignment()

  const resolvedRecipient = useRecipientResolution(latestMessageToPreview ? latestMessageToPreview.authorId : null)

  const isPreviewableMessage = useMemo(() => {
    if (gdpr.dismissedConsent || !latestMessageToPreview || !latestMessageToPreview.body || open) {
      return false
    }

    return true
  }, [latestMessageToPreview, open, gdpr.dismissedConsent])

  const setLatestMessageFromConvo = () => {
    if (isEmpty(unreadMessages)) {
      setLatestMessage(null)
      return
    }

    const lastMessage: Partial<Message> = unreadMessages.pop() || {}

    if (!latestMessageToPreview || !equals(lastMessage.id, latestMessageToPreview.id)) {
      setLatestMessage(truncateMessagePreview(lastMessage, PREVIEW_MESSAGE_LENGTH_LIMIT))
    }
  }

  const updateFrameSize = () => {
    if (!isPreviewableMessage) {
      return
    }

    const previewWidth = previewRefRect ? previewRefRect.width : 0
    const previewHeight = previewRefRect ? previewRefRect.height : 0

    const controllerWidth = previewWidth + DEFAULT_WIDGET_ICON_WIDTH + PREVIEW_MESSAGE_WIDTH_PADDING
    const controllerHeight = previewHeight + PREVIEW_MESSAGE_HEIGHT_PADDING

    setControllerDimensions(controllerHeight, controllerWidth)
  }

  useEffect(setLatestMessageFromConvo, [unreadMessages])
  useLayoutEffect(updateFrameSize, [previewRefRect, isPreviewableMessage])

  const isBotMessage = useIsBotMessage(latestMessageToPreview)

  if (!isPreviewableMessage) {
    return null
  }

  /**
   * Dismisses the preview of the message being displayed
   * When this happens we'll want to mark the conversation as read and reset the size of the controller frame so it's not taking up excess space
   */
  const dismiss = () => {
    if (!latestMessageToPreview) {
      return
    }

    markConversationAsRead(activeConversation)
    setLatestMessage({})
    resetControllerSize()
    dismissPlaybookIfNotInteracted()
  }

  /**
   * If a playbook has not been interacted with yet outside of being dismissed send out API events and mark the campaign as complete
   * This is used to tell the server that the playbook has been interacted with so ONCE_PER_SESSION playbooks will not fire again
   */
  const dismissPlaybookIfNotInteracted = () => {
    if (hasActiveConversationBeenInteractedWith()) {
      return
    }

    const playbookId = pathOr(null, ['attributes', 'playbookId'], activeCampaign)

    triggerPlaybookDismissedAPIEvent(latestMessageToPreview)
    storeAnnouncementCampaignCompleted(activeCampaign.id, playbookId)
  }

  const openChat = () => {
    resetControllerSize()

    if (isBotMessage) {
      const playbookId = pathOr(null, ['attributes', 'playbookId'], activeCampaign)

      triggerPlaybookClickedAPIEvent(latestMessageToPreview)
      storeAnnouncementCampaignCompleted(activeCampaign.id, playbookId)
    }

    dispatch(toggleChat({ chatOpen: true }))
  }

  const messagePreviewText = getMessagePreviewText(latestMessageToPreview)
  const ariaLabel = getNewMessageAriaLabel(resolvedRecipient.recipient.name, messagePreviewText)

  if (!widgetIconIsVisible) {
    return null
  }

  return (
    <div className="drift-widget-message-preview-wrapper" key={`${latestMessageToPreview.id}-preview-box`}>
      <div className={`drift-widget-message-preview drift-widget-message-preview--align-${alignment}`}>
        {alignment === WidgetAlignment.RIGHT &&
          <MessageCloseButton onClick={dismiss} />
        }
        <div
          onClick={openChat}
          onKeyDown={onKeyDownSubmit(openChat)}
          className={`drift-widget-message-preview-text drift-widget-message-preview-text--align-${alignment}`}
          aria-label={`${ariaLabel ? ariaLabel + ' - ' : ''}Open chat`}
          aria-live="polite"
          tabIndex={0}
          role="button"
        >
          <span ref={previewRef}>
            {messagePreviewText}
          </span>
        </div>
        {alignment === WidgetAlignment.LEFT &&
          <MessageCloseButton onClick={dismiss} />
        }
      </div>
    </div>
  )
}

export default MessagePreview
